import React, { useState, useEffect } from "react";
import MobileWrapper from "../../components/MobileWrapper";
import {
  getAllDocsSnap,
  updateDocById,
  db,
} from "../../utils/firebase/Firebase.utils";
import { collection, query, where, getDocs } from "firebase/firestore";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { sendBulkNotifications } from "../../utils/notifications";
import {
  convertTimeStamptoDate,
  dualNotification,
} from "../../utils/EleghoUtils";
import {
  FaChevronLeft,
  FaPlus,
  FaSearch,
  FaTimes,
  FaBox,
  FaCalendarAlt,
  FaClock,
  FaHome,
  FaTimesCircle,
  FaCheckCircle,
  FaImage,
  FaSpinner,
  FaShare,
  FaUser,
  FaUserCheck,
  FaCalendarCheck,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { appURL } from "../../utils/constants";

export default function ParcelLogs() {
  const currentUser = useSelector((state) => state.userObject);
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const navigate = useNavigate();

  // Filter options
  const [filter, setFilter] = useState("all"); // "all", "stored", "claimed"
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // Subscribe to real-time updates from the parcels collection
    const unsubscribe = getAllDocsSnap("parcels", (parcelData) => {
      // Sort by dateTime (newest first)
      const sortedParcels = parcelData.sort(
        (a, b) => new Date(b.dateTime) - new Date(a.dateTime)
      );
      setParcels(sortedParcels);
      setLoading(false);
    });

    // Cleanup subscription on component unmount
    return () => unsubscribe && unsubscribe();
  }, []);

  const filteredParcels = parcels
    .filter(
      filter === "all"
        ? () => true
        : (parcel) => parcel.status.toLowerCase() === filter.toLowerCase()
    )
    .filter((parcel) => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase().trim();
      const unitMatches = parcel.unitNumber?.toLowerCase().includes(query);
      const nameMatches = parcel.recipientName?.toLowerCase().includes(query);
      const parcelNumberMatches = parcel.parcelNumber
        ?.toLowerCase()
        .includes(query);

      return unitMatches || nameMatches || parcelNumberMatches;
    });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Calculate days unclaimed
  const calculateDaysUnclaimed = (dateString) => {
    const deliveryDate = new Date(dateString);
    const today = new Date();

    // Calculate the difference in milliseconds
    const differenceMs = today - deliveryDate;

    // Convert to days and round to nearest whole number
    const days = Math.floor(differenceMs / (1000 * 60 * 60 * 24));

    return days;
  };

  const handleStatusChange = async (e, parcelId, newStatus) => {
    e.stopPropagation(); // Prevent event bubbling
    if (window.confirm("Are you sure you want to change the status?")) {
      try {
        await updateDocById("parcels", parcelId, {
          status: newStatus,
          releasedBy: currentUser.email,
          releasedByName: currentUser.displayName,
          releasedDateTime: new Date(),
          collectedAt:
            newStatus === "Claimed" ? new Date().toISOString() : null,
        });
        toast.success(`Parcel marked as ${newStatus}`);
      } catch (error) {
        console.error("Error updating parcel status:", error);
        toast.error("Failed to update parcel status");
      }
    }
  };

  const handleOpenImage = (e, imageUrl) => {
    e.stopPropagation(); // Prevent event bubbling
    setSelectedImage(imageUrl);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  const handleSendReminder = async (e, parcel) => {
    e.stopPropagation(); // Prevent event bubbling
    setSendingReminder(parcel.id);
    try {
      // Find users associated with this unit
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("linkedIdentifiers", "array-contains", parcel.unitNumber)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast.error(`No users found for unit ${parcel.unitNumber}`);
        setSendingReminder(null);
        return;
      }

      // Collect user emails for notification
      const userEmails = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.email) {
          userEmails.push(userData.email);
        }
      });

      if (userEmails.length === 0) {
        toast.error(
          `No users with valid email found for unit ${parcel.unitNumber}`
        );
        setSendingReminder(null);
        return;
      }

      // Create reminder notification
      const daysUnclaimed = calculateDaysUnclaimed(parcel.dateTime);

      await Promise.all(
        userEmails.map(async (email) => {
          const notificationObject = {
            recipient: email,
            recipientName: parcel.recipientName,
            sender: currentUser.email,
            senderName: currentUser.displayName,
            subject: "Parcel Collection Reminder",
            message: `You have a parcel that has been unclaimed for ${daysUnclaimed} days. Please collect it from the reception.`,
            url: `/parcel/${parcel.id}`,
            type: "parcel_reminder",
            id: parcel.id,
            read: false,
            dateTime: new Date(),
          };

          await dualNotification(notificationObject);
        })
      );

      // Update the parcel document with reminder info
      await updateDocById("parcels", parcel.id, {
        lastReminderSent: new Date().toISOString(),
      });

      toast.success(
        `Reminder sent to ${userEmails.length} users for Unit ${parcel.unitNumber}`
      );
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error("Failed to send reminder notification");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleShare = async (e, parcel) => {
    e.stopPropagation(); // Prevent event bubbling
    const shareData = {
      title: "New Parcel Delivery",
      text: `You have a new parcel delivered and stored at ${
        parcel.storageLocation || "our facility"
      }. See details at ${appURL}/parcel-notice/${parcel.id}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(shareData.text);
        toast.success("Link copied to clipboard!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  // Count for each status type
  const getStatusCount = (status) => {
    return parcels.filter(
      (parcel) => parcel.status?.toLowerCase() === status.toLowerCase()
    ).length;
  };

  const allowedUsers = ["admin", "reception", "security"];

  return (
    <MobileWrapper label="Parcel Logs">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaBox className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Parcel Management
              </h1>
              <p className="text-xs text-gray-500">
                Track parcel deliveries and collections
              </p>
            </div>
          </div>
          <Link
            to="/parcels"
            className="flex items-center px-3 py-2 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
          >
            <FaPlus className="w-3 h-3 mr-1.5" />
            New
          </Link>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search by unit, name, or parcel number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchQuery("")}
            >
              <FaTimesCircle className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Status Filter Buttons */}
        <div className="mb-4 overflow-x-auto">
          <div className="flex space-x-2 pb-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filter === "all"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setFilter("all")}
            >
              All ({parcels.length})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filter === "stored"
                  ? "bg-[#8FCD00] text-white"
                  : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
              }`}
              onClick={() => setFilter("stored")}
            >
              Stored ({getStatusCount("stored")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filter === "claimed"
                  ? "bg-[#4299E1] text-white"
                  : "bg-[#4299E1]/10 text-gray-700 hover:bg-[#4299E1]/20"
              }`}
              onClick={() => setFilter("claimed")}
            >
              Claimed ({getStatusCount("claimed")})
            </button>
          </div>
        </div>

        {/* Parcel Cards */}
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
                <p className="mt-2 text-sm text-[#363062]">
                  Loading parcels...
                </p>
              </div>
            </div>
          ) : filteredParcels.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaBox className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {parcels.length === 0 ? "No Parcels" : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {parcels.length === 0
                  ? "No parcels have been logged yet"
                  : "Try adjusting your filters or search terms"}
              </p>
            </div>
          ) : (
            filteredParcels.map((parcel) => (
              <div
                key={parcel.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-[#363062]">
                      {parcel.parcelNumber
                        ? `Parcel #${parcel.parcelNumber}`
                        : "Unnamed Parcel"}
                    </div>
                    <div
                      className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
                      style={{
                        backgroundColor:
                          parcel.status?.toLowerCase() === "stored"
                            ? "#8FCD0015"
                            : "#4299E115",
                        color:
                          parcel.status?.toLowerCase() === "stored"
                            ? "#8FCD00"
                            : "#4299E1",
                      }}
                    >
                      <span className="text-xs">
                        {parcel.status?.toLowerCase() === "stored" ? (
                          <FaClock />
                        ) : (
                          <FaCheckCircle />
                        )}
                      </span>
                      {parcel.status || "Unknown"}
                    </div>
                  </div>

                  <div className="flex items-center mb-2">
                    <div className="text-xs font-medium text-gray-600">
                      {parcel.recipientName || "Unnamed Recipient"}
                    </div>
                    {parcel.unitNumber && (
                      <div className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">
                        {parcel.unitNumber}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-gray-400 flex-shrink-0" />
                      <span>{formatDate(parcel.dateTime).split(",")[0]}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaClock className="text-gray-400 flex-shrink-0" />
                      <span>{formatDate(parcel.dateTime).split(",")[1]}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaHome className="text-gray-400 flex-shrink-0" />
                      <span>{parcel.storageLocation || "Not specified"}</span>
                    </div>

                    {parcel.createdBy && (
                      <div className="flex items-center gap-1.5">
                        <FaUser className="text-gray-400 flex-shrink-0" />
                        <span>{parcel.createdByName || parcel.createdBy}</span>
                      </div>
                    )}

                    {parcel.status?.toLowerCase() === "claimed" &&
                      parcel.releasedByName && (
                        <div className="flex items-center gap-1.5">
                          <FaUserCheck className="text-gray-400 flex-shrink-0" />
                          <span>Released by: {parcel.releasedByName}</span>
                        </div>
                      )}

                    {parcel.status?.toLowerCase() === "claimed" &&
                      parcel.releasedDateTime && (
                        <div className="flex items-center gap-1.5">
                          <FaCalendarCheck className="text-gray-400 flex-shrink-0" />
                          <span>
                            Released:{" "}
                            {convertTimeStamptoDate(parcel.releasedDateTime)}
                          </span>
                        </div>
                      )}
                  </div>

                  {/* Days unclaimed for stored parcels */}
                  {parcel.status?.toLowerCase() === "stored" && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div
                        className={`text-xs font-medium rounded-full px-2 py-1 inline-flex items-center ${
                          calculateDaysUnclaimed(parcel.dateTime) > 7
                            ? "bg-red-100 text-red-700"
                            : calculateDaysUnclaimed(parcel.dateTime) > 3
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        <FaClock className="mr-1 w-3 h-3" />
                        {calculateDaysUnclaimed(parcel.dateTime)} days unclaimed
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions Section */}
                <div className="py-3 px-4 bg-gray-50 border-t border-gray-100">
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2">
                      {currentUser.roles.some((role) =>
                        allowedUsers.includes(role)
                      ) &&
                        parcel.status === "Stored" && (
                          <button
                            className="px-3 py-1.5 text-xs bg-[#4299E1] text-white rounded-md hover:bg-[#4299E1]/80 transition flex items-center"
                            onClick={(e) =>
                              handleStatusChange(e, parcel.id, "Claimed")
                            }
                          >
                            <FaCheckCircle className="mr-1 w-3 h-3" />
                            Mark as Claimed
                          </button>
                        )}

                      {parcel.status?.toLowerCase() === "stored" &&
                        calculateDaysUnclaimed(parcel.dateTime) > 3 && (
                          <button
                            className="px-3 py-1.5 text-xs bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition flex items-center"
                            onClick={(e) => handleSendReminder(e, parcel)}
                            disabled={sendingReminder === parcel.id}
                          >
                            {sendingReminder === parcel.id ? (
                              <>
                                <FaSpinner className="animate-spin mr-1 w-3 h-3" />
                                <span>Sending...</span>
                              </>
                            ) : (
                              <>
                                <FaClock className="mr-1 w-3 h-3" />
                                Send Reminder
                              </>
                            )}
                          </button>
                        )}
                    </div>

                    {/* Utility buttons */}
                    <div className="flex items-center gap-2">
                      {parcel.imageUrl && (
                        <button
                          className="px-2 py-1 text-xs text-[#363062] bg-[#363062]/10 rounded-md hover:bg-[#363062]/20 transition flex items-center"
                          onClick={(e) => handleOpenImage(e, parcel.imageUrl)}
                        >
                          <FaImage className="mr-1 w-3 h-3" />
                          View Photo
                        </button>
                      )}
                      <button
                        className="px-2 py-1 text-xs text-[#8FCD00] bg-[#8FCD00]/10 rounded-md hover:bg-[#8FCD00]/20 transition flex items-center"
                        onClick={(e) => handleShare(e, parcel)}
                      >
                        <FaShare className="mr-1 w-3 h-3" />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Image Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden relative">
              <button
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 p-2 z-10 bg-white bg-opacity-75 rounded-full"
                onClick={handleCloseModal}
              >
                <FaTimes className="h-6 w-6" />
              </button>
              <div className="p-1 flex items-center justify-center overflow-auto max-h-[80vh]">
                <img
                  src={selectedImage}
                  alt="Parcel"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileWrapper>
  );
}
